/**
 * @file LocalPickupFormScreen.tsx
 *
 * Routed wrapper around {@link LocalPickupForm} for the
 * `/shipping/local-pickup/new` and `/:id/edit` routes. Seeds the form from the
 * `/v1` pickup locations in the store (cache-first; fetches the pickup locations and
 * the region tree when missing), shows loading/load-error states, and owns
 * persistence: on save it creates (append) or updates (by id) the location, toasts the
 * save outcome, then navigates back to the overview. The seed is memoized so the
 * form's controlled state isn't reset on every render.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { notifyError, notifySuccess } from '../../feedback/toast';
import { useShippingStore } from '../../stores/shipping.store';
import type { PickupLocationInput } from '../../api/shop-types';
import { EmptyState } from '../../components/EmptyState';
import { createEmptyPickupLocation } from '../shipping-defaults';
import { useLocalizedContinents } from '../use-localized-continents';
import { LocalPickupForm, type LocalPickupFormMode } from './LocalPickupForm';

interface LocalPickupFormScreenProps {
  mode: LocalPickupFormMode;
}

/**
 * Renders the routed local-pickup form for the given mode.
 *
 * @param props - The form mode (create/edit).
 *
 * @returns The pickup form screen (with loading/error fallbacks).
 */
export function LocalPickupFormScreen({ mode }: LocalPickupFormScreenProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { id } = useParams();

  const pickups = useShippingStore((s) => s.pickupLocations);
  const continents = useLocalizedContinents();
  const pickupLoading = useShippingStore((s) => s.pickupLoading);
  const fetchPickupLocations = useShippingStore((s) => s.fetchPickupLocations);
  const fetchContinents = useShippingStore((s) => s.fetchContinents);
  const createPickup = useShippingStore((s) => s.createPickup);
  const updatePickup = useShippingStore((s) => s.updatePickup);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    fetchPickupLocations().finally(() => setFetched(true));
    if (continents.length === 0) {
      fetchContinents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editBase =
    mode === 'edit' && id !== undefined ? pickups.find((p) => p.id === id) : undefined;

  const seed: PickupLocationInput = useMemo(
    () =>
      editBase
        ? { name: editBase.name, address: editBase.address, details: editBase.details }
        : createEmptyPickupLocation(),
    [editBase],
  );

  const goBack = (): void => {
    navigate('/shipping');
  };

  const handleSubmit = async (location: PickupLocationInput): Promise<void> => {
    setSaving(true);
    setSaveError(undefined);
    const ok =
      mode === 'edit' && editBase
        ? await updatePickup(editBase.id, location)
        : (await createPickup(location)) !== null;
    setSaving(false);
    if (!ok) {
      setSaveError(intl.formatMessage(adminMessages.shippingPickupSaveError));
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.toastSaved));
    goBack();
  };

  const seedReady = mode === 'create' || editBase !== undefined;

  if (!seedReady) {
    if (pickupLoading || !fetched) {
      return (
        <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
          <Loader>
            <FormattedMessage {...adminMessages.shippingLoading} />
          </Loader>
        </div>
      );
    }
    return (
      <EmptyState
        title={intl.formatMessage(adminMessages.shippingPickupLoadErrorTitle)}
        description={intl.formatMessage(adminMessages.shippingPickupLoadErrorDescription)}
        action={{
          label: intl.formatMessage(adminMessages.shippingTitle),
          onClick: goBack,
        }}
      />
    );
  }

  return (
    <LocalPickupForm
      mode={mode}
      seed={seed}
      continents={continents}
      saving={saving}
      saveError={saveError}
      onSubmit={handleSubmit}
      onCancel={goBack}
    />
  );
}
